import { Filter } from './filters';
import { User } from './users';

export interface Tendering {
  tenderingID: number;
  tenderingName: string;
  properties: TenderingProperties;
  tenderingParts: TenderingPart[];
  baseFilters?: Filter[];
  filters?: Filter[];
  indexing?: TenderingIndexing[];
  tenderingContractFee?: TenderingFee;
  tenderingFeeReports?: TenderingFeeReportPeriod[];
  archivingUsers?: number[];
  [key: string]: any;
}

export interface TenderingProperties {
  factMapping?: TenderingFactMapping[];
  factSettings?: TenderingFactSetting;
  tenderingDescription?: string;
  tenderingProcessText?: string;
  tenderingAutoPlacementText?: string;
  tenderingManualPlacementText?: string;
  placementOutsideTenderingWarning?: string;
  powerAutomateUrl?: string;
  questionMappings?: any;
  showTenderList?: boolean;
  plannedActivationDate?: string;
  // System users (module 1) allowed to see this tendering. Undefined means no restriction (everyone).
  allowedSystemUserIDs?: number[];
  contract: TenderContract;
  [key: string]: any;
}

export interface TenderingFactMapping {
  questionID: number;
  heading: string;
  text: string;
  question?: Question;
  answer?: TenderAnswer;
  tenderingPartID: number;
  section: string;
  permission: string;
  pendingChange?: boolean;
}

export interface TenderingFactSetting {
  sections?: FactSection[];
}

export interface FactSection {
  name: string;
  mappings: TenderingFactMapping[];
  areNegativeAnswersHidden?: boolean;
  autoOpenMapping?: boolean;
  showInFacts?: boolean;
  showInHiddenFacts?: boolean;
}

export interface TenderingPart {
  tenderingPartID: number;
  evaluated: boolean;
  activateOnContractStart?: boolean;
  tenderingPart: string;
  allowedOrganizations?: number[];
  questions?: Question[];
  [key: string]: any;
}

export interface TenderingIndexing {
  indexingName: string;
  indexingID: number;
  indexingDescription?: string;
  startDate?: string;
  endDate?: string;
  indexingDate?: string;
  index?: number;
  prices?: Question[];
  open?: boolean;
}

export interface TenderingFee {
  feeStartDate: string;
  reportPeriodInterval: number;
  startDay: number;
  nrOfDaysReportOpen: number;
  hideOnOverdue: boolean;
  notifyText: string;
  notifyDay: number;
  reminderText: string;
  reminderDay: number;
  reminderInterval: number;
  billingText: string;
  billingDay: number;
  costCenter: string;
  account: string;
  object: string;
  deadline: number;
  penaltyFineInterval?: string;
  penaltyFineAmount?: number;
  maxPenaltyFineType?: string; 
  maxPenaltyFineAmount?: number;
  measureReportType: string;
  measures?: TenderingFeeMeasure[];
  billingUrl?: string;
}

export interface TenderingFeeReportPeriod {
  reportID: string;
  tenderingID: number;
  periodStart: string;
  periodEnd: string;
  notifyDate: string;
  reminderStartDate: string;
  billingDate: string;
  header: string;
  deadlineDate: string;
  state: string;
  status: string;
}

export interface TenderFeePeriodReport {
  reportID: string;
  tenderID: number;
  tender: string;
  status: string;
  nextState: string;
  payed: number;
  owed: number;
  report?: TenderFeeReport[];
  sent?: boolean;
}

export interface TenderFeeReport {
  value: number;
  quantity?: number;
  procurer: any; 
  measure: TenderingFeeMeasure;
  user: User;
  price: number;
}

export enum TenderingFeePenaltyFineIntervalType {
  Once = 'Enbart en gång',
  Daily = 'Dag',
  Weekly = 'Vecka',
  Monthly = 'Månad'
}

export enum TenderingFeeMaxPenaltyFineType {
  Fixed = 'Fast',
  Share = 'Andel av pris'
}

export interface TenderingFeeMeasure {
  id: string;
  header: string;
  valueType: string;
  feeType: string;
  feeValue: number;
  valueQuestion?: number;
  allowEmptyReport: boolean;
}

export const TenderFeeStatuses = {
  Notified: {status: 'Notifierad', next: 'Väntar inrapportering'},
  Reported: {status: 'Inrapporterat',  next: 'Skicka faktura'},
  //ReportApproved: {status: 'Inrapportering godkänd', next: 'Skicka faktura'},
  Billed: {status: 'Fakturerad', next: 'Väntar inbetalning'},
  Payed: {status: 'Inbetalt', next: ''},
  Overdue: {status: 'Passerad', next: 'Väntar inbetalning'},
};

export const ReportPeriodStates = {
  Planned: 'Planerad',
  Notified: 'Notifierad',
  Reported: 'Inrapporterat',
  Billed: 'Fakturerad',
  Payed: 'Inbetalt',
  Overdue: 'Passerad',
  Ended: 'Avslutad'
};

export enum TenderingFeeValueType {
  Hours = 'Timmar',
  Days = 'Dagar',
  Unit = 'Antal'
}

export enum TenderingFeeType {
  Percent = 'Procent',
  Fixed = 'Fastpris'
}

export enum TenderingFeeReportType {
  Separate = 'Per kommun',
  All = 'Alla tillsammans'
}

export interface Tender {
  tenderID: number;
  tenderingID: number;
  tenderName: string;
  tenderProperties: {
    hidden: boolean;
    unpublished: boolean;
  };
  tenderingParts: TenderingPart[];
  tenderAnswers?: TenderAnswer[];
  indexing?: TenderIndexing[];
  contact?: TenderContact;
  tenderManagerTool?: TenderManagerTool;
  reportPeriods?: TenderFeePeriodReport[];
  [key: string]: any;
  archivingUsers: number[];
}

export interface TenderContact{
  phone?: string;
  email?: string;
}

export interface TenderAnswer {
  questionID: number;
  tenderID: number;
  type: string;
  value: any;
  comment?: string;
  // Written by TenderingUtility/PriceIndexing: [rankingValue, valueBeforeIndexing1, valueBeforeIndexing2, ...]
  valueHistory?: (string | number | null)[];
  [key: string]: any;
}

export interface TenderManagerTool {
  tenderID: number;
  tenderingID: number;
  planned: {
    date: string;
    text: string;
  },
  followedUp: {
    date: string;
    text: string;
  },
  tenderingManager?:number[];
  tendering: string;
  tenderName: string;
  hidden: boolean;
  unpublished: boolean;
  organization: string;
  organizationNr: number;
  adress: string;
  contactName?: string;
  prio: {
    prio: number;
    text: string;
  };
  statuses: number[];
} 

export interface TenderingManager {
  userID: number;
  displayName: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  color: string;
}

export interface TenderIndexing {
  indexingName: string;
  indexingID: number;
  tenderName: string;
  tenderID: number;
  user: string;
  startDate?: string;
  endDate?: string;
  indexingDate?: string;
  requestDate?: Date;
  index?: number;
  prices?: Question[];
  receipt: string;
}

export interface TenderContract{
  fields: ContractField[];
  isActivated: boolean;
  template: string;
}

export interface ContractField{
  fieldName: string;
  fieldType: string;
  value?: string;
  questionID?: number | null;
  isDefaultField?: boolean
  isDisabled?: boolean;
  isRequired?: boolean;
  isReportData?: boolean;
}

export interface Question {
  heading: string;
  text: string;
  questionID: number;
  tenderingPartID: number;
  questionType: string;
}

export enum RankingType {
  Price = 1,
  RankingValue = 2
}
